import { useEffect, useState } from 'react';
import { api, type StoredScan, type Vehicle } from '../lib/api.ts';
import type { ScanResult } from '../lib/elm327.ts';
import type { Dtc, DtcSeverity } from '../lib/obd/dtc.ts';
import { SummaryPanel } from './SummaryPanel.tsx';
import { PIDS, formatVital } from '../lib/obd/pids.ts';
import { CalibrationPanel, MonitorTestsPanel, ReadinessPanel } from './DiagnosticsPanels.tsx';
import { VehicleExtras, VehicleHeader } from './VehicleCard.tsx';
import { VehicleAccess } from './VehicleAccess.tsx';
import { formatDay, formatStamp, usePrefs } from '../lib/prefs.tsx';
import type { UnitSystem } from '../lib/obd/pids.ts';
import { RecallsPanel } from './RecallsPanel.tsx';
import { MaintenancePanel } from './MaintenancePanel.tsx';
import { Term } from './Term.tsx';
import { PartsPanel } from './PartsPanel.tsx';
import { LivePanel } from './LivePanel.tsx';

interface LiveProps {
  live: boolean;
  liveKeys: string[];
  samples: Record<string, number[]>;
  liveHz: number | null;
  onToggleLiveKey: (key: string) => void;
  /** The scan the open connection belongs to, so only that entry hosts it. */
  liveScanId: number | null;
}

export function HistoryView({
  refreshKey, selectedVin, isOwner = false, isCustomer = false, ...liveProps
}: { refreshKey: number; selectedVin: string | null; isOwner?: boolean; isCustomer?: boolean } & LiveProps) {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  // The list lives in the sidebar now. This view follows it rather than
  // keeping a second copy of the same five vehicles three feet away.
  const selected = selectedVin;

  // A new vehicle starts at the top. Keeping the scroll position drops you into
  // the middle of a different car's history at whatever depth you had reached in
  // the last one, which is never where you meant to be.
  //
  // Instant rather than smooth: this is a change of subject, not a move within
  // one, and animating it makes the old vehicle appear to scroll past.
  const [scans, setScans] = useState<StoredScan[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  // Which codes across this vehicle's whole history resolve to parts. Asked
  // once for the union rather than per scan, since the answer depends only on
  // the code and the same codes recur across scans.
  const [mappedCodes, setMappedCodes] = useState<Set<string>>(new Set());
  // The newest scan opens on arrival, so the current state of the vehicle is
  // what you see rather than a collapsed row to click. Only when nothing is
  // open, so it never closes something you chose.
  useEffect(() => {
    setExpanded((current) => (current === null ? (scans[0]?.id ?? null) : current));
  }, [scans]);
  const { dateFormat, units } = usePrefs();

  useEffect(() => {
    setLoading(true);
    api
      .listVehicles()
      .then(setVehicles)
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [refreshKey]);

  useEffect(() => {
    if (!selected) {
      setScans([]);
      return;
    }
    api.vehicleScans(selected).then(setScans).catch(() => setScans([]));
  }, [selected, refreshKey]);

  useEffect(() => {
    const codes = [...new Set(scans.flatMap((s) => s.dtcs.map((d) => d.code)))];
    if (codes.length === 0) {
      setMappedCodes(new Set());
      return;
    }
    let cancelled = false;
    api
      .mappedCodes(codes)
      .then((r) => !cancelled && setMappedCodes(new Set(r.mapped)))
      .catch(() => !cancelled && setMappedCodes(new Set()));
    return () => {
      cancelled = true;
    };
  }, [scans]);

  // Scroll once the vehicle's scans have arrived, not when the selection
  // changes. The column mounts before its data does, so on the first pick of a
  // session it was still a placeholder when the scroll ran, and the measurement
  // it took was of a view that had not been built yet. Every later pick worked
  // because the previous vehicle's content was still on the page.
  useEffect(() => {
    if (!selectedVin) return;

    // Beside the content on a wide screen, so the top of the page is the top of
    // the vehicle. Stacked under the fleet list on a phone, where the top of
    // the page is the list you just chose from.
    if (!window.matchMedia('(max-width: 900px)').matches) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }

    const main = document.querySelector('.history-detail');
    if (!main) return;

    const header = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-h'), 10) || 56;
    window.scrollTo({ top: Math.max(0, main.getBoundingClientRect().top + window.scrollY - header - 8), behavior: 'auto' });
  }, [selectedVin, scans]);

  const selectedVehicle = vehicles.find((v) => v.vin === selected) ?? null;


  if (loading) return <p className="dim">Loading history…</p>;

  // No vehicle chosen yet. This is the first thing someone sees, so it says
  // what to do rather than showing an empty frame.
  if (!selected) {
    return (
      <div className="empty">
        {/* A customer cannot scan and has no fleet, so the technician's version
            of this told them to connect an adapter they will never own. */}
        {isCustomer ? (
          <>
            <strong>Choose a vehicle</strong>
            Pick one on the left to see what its last scan found, whether any recalls are open, and what service is
            coming up.
          </>
        ) : (
          <>
            <strong>Pick a vehicle, or scan one</strong>
            Choose from the fleet on the left to see its history, recalls and maintenance. To add a vehicle, connect
            an adapter and press Scan vehicle — the VIN is read during the scan, so the result attaches itself to the
            right record once it is known.
          </>
        )}
      </div>
    );
  }

  if (vehicles.length === 0) {
    return (
      <div className="empty">
        <strong>No vehicles yet</strong>
        Every scan that reports a VIN creates a profile here, and each later scan is diffed against the one before it.
      </div>
    );
  }

  return (
    <div className="history single">
      <div className="stack history-detail" style={{ gap: 20 }}>
      {selectedVehicle && (
        <nav className="section-nav" aria-label="Sections">
          <a href="#veh-summary">Summary</a>
          <a href="#veh-details">Details</a>
          <a href="#veh-recalls">Recalls</a>
          <a href="#veh-maint">Maintenance</a>
          <a href="#veh-scans">Scan history</a>
          {/* Owners only, and it has to match the panel below or the link
              scrolls to nothing. The panel hides itself for anyone else. */}
          {isOwner && <a href="#veh-access">Owner access</a>}
        </nav>
      )}

      {/* The latest scan's verdict, first on the page.
          It is the question someone opens a vehicle to answer, and inside a
          scan entry it sat below the details, the recalls, the maintenance and
          the scan list. The panel collapses, so it costs one line when it is
          not what you came for. */}
      {selectedVehicle && scans[0] && (
        <div id="veh-summary">
          {/* Inside the panel, under the headline and above the findings, so
              the verdict is followed immediately by what to do about it. A
              second copy sits after the findings for anyone who reads through
              before acting. */}
          <SummaryPanel
            result={toScanResult(scans[0])}
            vehicle={selectedVehicle}
            scanId={scans[0].id}
            action={<PartsAction scan={scans[0]} onExpand={setExpanded} top />}
          />

          <PartsAction scan={scans[0]} onExpand={setExpanded} />
        </div>
      )}

      {selectedVehicle && (
        <div className="card" id="veh-details">
          <VehicleHeader vehicle={selectedVehicle} vin={selectedVehicle.vin} />
          {/* Who is looking after this car. Only a customer sees it, and it
              belongs here rather than only in the sidebar list: this is the
              page somebody reads about their own vehicle, and "who has it" is
              among the first things they want to know. */}
          {selectedVehicle.providers && selectedVehicle.providers.length > 0 && (
            <p className="shared-by small">
              {selectedVehicle.providers.length === 1 ? 'Looked after by ' : 'Looked after by '}
              <strong>{selectedVehicle.providers.join(' and ')}</strong>
              {selectedVehicle.sharedAt ? `, shared with you on ${formatDay(selectedVehicle.sharedAt, dateFormat)}` : ''}
              . Everything below was recorded by {selectedVehicle.providers.length === 1 ? 'them' : 'those providers'}.
            </p>
          )}
          <VehicleExtras
            vehicle={selectedVehicle}
            supportedPids={scans.find((s) => s.supportedPids?.length)?.supportedPids ?? []}
          />
        </div>
      )}

      {selectedVehicle && isOwner && <VehicleAccess vin={selectedVehicle.vin} />}

      {selectedVehicle && (
        <div id="veh-recalls">
          <RecallsPanel vehicle={selectedVehicle} />
        </div>
      )}

      {selectedVehicle && (
        <div id="veh-maint">
          <MaintenancePanel vehicle={selectedVehicle} />
        </div>
      )}


      {selected && (
        <div className="card" id="veh-scans">
          <h2>Scan history</h2>
          {scans.length === 0 ? (
            <p className="dim small">No scans recorded for this vehicle.</p>
          ) : (
            <div className="timeline">
              {scans.map((scan) => (
                <div className="scan-entry" key={scan.id}>
                  <div className="scan-row">
                  <span className="when">
                    {formatStamp(scan.startedAt, dateFormat)}
                    {scan.odometerKm !== null && (
                      <span className="odo-read" title="Odometer recorded at this scan">
                        {formatOdometer(scan.odometerKm, units)}
                      </span>
                    )}
                    {/* Named, or said to be unattributed. Scans taken before
                        accounts existed have nobody to credit, and inventing an
                        "unknown user" would imply somebody we cannot name. */}
                    {/* A customer sees the provider; staff see the person,
                        since inside one shop the company name is the same on
                        every row and tells them nothing. */}
                    {scan.provider ? (
                      <span className="scan-by">at {scan.provider}</span>
                    ) : (
                      scan.performedByName && <span className="scan-by">by {scan.performedByName}</span>
                    )}
                  </span>

                  <div className="stack" style={{ gap: 6 }}>
                    <div className="row wrap" style={{ gap: 6 }}>
                      {scan.dtcs.length === 0 ? (
                        <span className="pill ok">
                          <i className="dot" />
                          clean
                        </span>
                      ) : (
                        scan.dtcs.map((d) => (
                          <span className="mono small" key={`${scan.id}-${d.code}-${d.kind}`}>
                            {d.code}
                          </span>
                        ))
                      )}
                    </div>

                    {Boolean(scan.added?.length || scan.cleared?.length) && (
                      <div className="delta">
                        {scan.added?.map((c) => (
                          <span className="added" key={`a-${c}`}>
                            +{c}
                          </span>
                        ))}
                        {scan.cleared?.map((c) => (
                          <span className="removed" key={`r-${c}`}>
                            −{c}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="row" style={{ gap: 6 }}>
                    <span className={`pill ${scan.milOn ? 'warn' : 'ok'}`}>
                      <Term of="MIL">{scan.milOn ? 'MIL' : 'no MIL'}</Term>
                    </span>
                    {/* Same signal as the fleet list: codes say something is
                        wrong, this says there is a next step. */}
                    {scan.dtcs.some((d) => mappedCodes.has(d.code)) && (
                      <span className="pill accent" title="This scan has codes with parts listed">
                        <Term of="parts">parts</Term>
                      </span>
                    )}
                    {scan.readiness && (
                      <span className={`pill ${scan.readiness.ready ? 'ok' : 'warn'}`}>
                        {scan.readiness.ready ? 'ready' : `${scan.readiness.incompleteCount} pending`}
                      </span>
                    )}
                    <button className="tiny ghost" onClick={() => setExpanded(expanded === scan.id ? null : scan.id)}>
                      {expanded === scan.id ? 'less' : 'detail'}
                    </button>
                    <button
                      className="tiny danger"
                      onClick={async () => {
                        await api.deleteScan(scan.id);
                        setScans((prev) => prev.filter((s) => s.id !== scan.id));
                      }}
                    >
                      delete
                    </button>
                  </div>
                </div>
                  {expanded === scan.id && (
                    <ScanDetail
                      scan={scan}
                      vehicle={selectedVehicle}
                      liveProps={liveProps}
                      onOdometer={(km) =>
                        setScans((prev) =>
                          prev.map((s) => (s.id === scan.id ? { ...s, odometerKm: km, odometerSource: km === null ? null : 'manual' } : s)),
                        )
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}


/**
 * From the verdict to what might fix it.
 *
 * Expands the latest scan, selects its most serious code and scrolls to the
 * parts for it. Two frames of waiting: one for the entry to expand, one for the
 * panel to render inside it, because scrolling before either has happened finds
 * nothing to scroll to.
 */
function PartsAction({
  scan, onExpand, top = false,
}: {
  scan: StoredScan;
  onExpand: (id: number) => void;
  top?: boolean;
}) {
  // Which codes the catalog can price. Severity alone is not enough: the most
  // serious fault may have no mapping, and sending someone to an empty panel is
  // worse than not offering the action.
  const [mapped, setMapped] = useState<string[] | null>(null);

  useEffect(() => {
    const codes = scan.dtcs.map((d) => d.code);
    if (codes.length === 0) return;
    let cancelled = false;
    api
      .mappedCodes(codes)
      .then((r) => !cancelled && setMapped(r.mapped))
      .catch(() => !cancelled && setMapped([]));
    return () => {
      cancelled = true;
    };
  }, [scan.id]);

  if (scan.dtcs.length === 0) return null;
  // Still checking, or nothing on this scan resolves to a part.
  if (mapped === null || mapped.length === 0) return null;

  return (
    <div className="row wrap" style={{ gap: 8, marginBottom: top ? 10 : 0, marginTop: top ? 0 : 10 }}>
      <button
        className="primary"
        onClick={() => {
          onExpand(scan.id);
          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              // The most severe code, not the first one rendered. The list is
              // in the order the vehicle reported it, so position says nothing
              // about which fault matters.
              const rank: Record<string, number> = { critical: 0, high: 1, moderate: 2, low: 3 };
              const worst = [...scan.dtcs]
                .filter((d) => mapped.includes(d.code))
                .sort((a, b) => (rank[a.severity ?? 'moderate'] ?? 2) - (rank[b.severity ?? 'moderate'] ?? 2))[0];
              if (!worst) return;

              const rows = [...document.querySelectorAll<HTMLButtonElement>(`#s${scan.id}-codes ~ .dtc-list .dtc`)];
              const code = rows.find((r) => r.querySelector('.code')?.textContent === worst.code) ?? rows[0];

              // Open it, do not toggle it. Clicking unconditionally closed a
              // panel that was already open, which shortens the page and reads
              // as the view collapsing under you.
              if (code && code.getAttribute('aria-pressed') !== 'true') code.click();

              // Scroll once the panel has stopped growing, not once it exists.
              // It fetches its offers after mounting, so scrolling immediately
              // aims at an element that is still a placeholder and ends up
              // short of it by however tall the results turn out to be.
              scrollWhenSettled(`s${scan.id}-parts`);
            }),
          );
        }}
      >
        See recommended parts
      </button>
      <span className="small dim">
        For the most serious code with parts listed, ranked by how often each is the cause.
      </span>
    </div>
  );
}


/**
 * Scroll to an element once its height has settled.
 *
 * Panels that fetch their own contents mount short and grow, so a scroll issued
 * on mount lands above the thing it was aiming at. This waits for two
 * consecutive measurements to agree before scrolling, and gives up after a
 * couple of seconds so a slow request cannot leave the page stuck.
 */
function scrollWhenSettled(id: string, timeoutMs = 2500) {
  const started = Date.now();
  let lastHeight = -1;

  const tick = () => {
    const el = document.getElementById(id);
    if (!el) {
      if (Date.now() - started < timeoutMs) requestAnimationFrame(tick);
      return;
    }

    const height = Math.round(el.getBoundingClientRect().height);
    const settled = height > 0 && height === lastHeight;

    if (settled || Date.now() - started > timeoutMs) {
      el.scrollIntoView({ block: 'start', behavior: 'smooth' });
      return;
    }

    lastHeight = height;
    // Every other frame, so two samples are not taken inside one paint.
    setTimeout(tick, 80);
  };

  requestAnimationFrame(tick);
}

const MI_PER_KM = 0.621371;

/** Grouped digits, because a six-figure reading is unreadable without them. */
function formatOdometer(km: number, units: UnitSystem): string {
  const value = units === 'imperial' ? km * MI_PER_KM : km;
  return `${Math.round(value).toLocaleString()} ${units === 'imperial' ? 'mi' : 'km'}`;
}

/**
 * Odometer entry, per scan.
 *
 * No vehicle scanned so far reports PID 0xA6, so this is the only way the
 * reading gets in. It sits on each scan rather than on the vehicle because two
 * readings and their dates give a usage rate, which is what turns "due in 2,000
 * miles" into a date someone can book a bay against.
 *
 * Stored in kilometres like every other distance here; the field speaks
 * whichever unit the reader has chosen.
 */
function OdometerField({ scan, onSaved }: { scan: StoredScan; onSaved: (km: number | null) => void }) {
  const { units } = usePrefs();
  const imperial = units === 'imperial';
  const toDisplay = (km: number | null) => (km === null ? '' : String(Math.round(imperial ? km * MI_PER_KM : km)));

  const [value, setValue] = useState(() => toDisplay(scan.odometerKm));
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  // A unit switch changes what the same reading should read as.
  useEffect(() => {
    setValue(toDisplay(scan.odometerKm));
  }, [scan.odometerKm, imperial]);

  const save = async () => {
    const trimmed = value.trim();
    const entered = trimmed === '' ? null : Number(trimmed);

    if (entered !== null && (!Number.isFinite(entered) || entered < 0)) {
      setState('error');
      setMessage('Enter a positive number, or leave it blank to clear.');
      return;
    }

    const km = entered === null ? null : imperial ? entered / MI_PER_KM : entered;
    setState('saving');
    setMessage(null);
    try {
      await api.setScanOdometer(scan.id, km);
      onSaved(km);
      setState('saved');
    } catch (err) {
      setState('error');
      setMessage((err as Error).message);
    }
  };

  return (
    <div className="odo">
      <label className="field odo-field">
        <span>Odometer at this scan</span>
        <div className="odo-input">
          <input
            type="number"
            inputMode="numeric"
            min="0"
            value={value}
            placeholder={imperial ? 'miles' : 'km'}
            onChange={(e) => {
              setValue(e.target.value);
              setState('idle');
            }}
            onKeyDown={(e) => e.key === 'Enter' && save()}
          />
          <span className="unit">{imperial ? 'mi' : 'km'}</span>
          <button className="tiny" onClick={save} disabled={state === 'saving'}>
            {state === 'saving' ? 'saving…' : 'save'}
          </button>
        </div>
      </label>
      {state === 'saved' && <span className="small ok-text">Saved.</span>}
      {state === 'error' && message && <span className="small crit-text">{message}</span>}
      {scan.odometerSource && state !== 'error' && (
        <span className="small dim">Entered by hand — no scanned vehicle reports the odometer over OBD-II.</span>
      )}
    </div>
  );
}

/**
 * A stored scan holds everything the summary needs, but shaped for the
 * database rather than for the panel. Mapping one to the other lets History
 * reuse SummaryPanel unchanged, offline fallback included.
 */
function toScanResult(scan: StoredScan): ScanResult {
  const dtcs: Dtc[] = scan.dtcs.map((d) => ({
    code: d.code,
    kind: d.kind,
    description: d.description ?? d.code,
    system: d.system ?? '',
    severity: (d.severity as DtcSeverity | null) ?? 'moderate',
    // No stored description means it came from the range fallback, which is
    // the same thing `generic` records at scan time.
    generic: d.description === null,
  }));

  return {
    vin: scan.vin,
    protocol: scan.protocol,
    adapterId: null,
    milOn: scan.milOn,
    reportedDtcCount: scan.dtcCount,
    stored: dtcs.filter((d) => d.kind === 'stored'),
    pending: dtcs.filter((d) => d.kind === 'pending'),
    permanent: dtcs.filter((d) => d.kind === 'permanent'),
    vitals: scan.vitals ?? {},
    freezeFrame: scan.freezeFrame,
    readiness: scan.readiness,
    monitorTests: scan.monitorTests ?? [],
    odometerKm: null,
    odometerReadings: [],
    odometerDisagreement: false,
    vehicleInfo: scan.vehicleInfo,
    supportedPids: scan.supportedPids ?? [],
    adapterVoltage: scan.adapterVoltage,
    rawLog: scan.rawLog ?? '',
    startedAt: scan.startedAt,
    finishedAt: scan.finishedAt ?? scan.startedAt,
  };
}

/** Everything the scan captured, which until now was written and never read. */
function ScanDetail({
  scan,
  vehicle,
  onOdometer,
  liveProps,
}: {
  scan: StoredScan;
  vehicle: Vehicle | null;
  onOdometer: (km: number | null) => void;
  liveProps: LiveProps;
}) {
  const { units } = usePrefs();
  // Per scan entry, so two open scans each keep their own selection.
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const vitals = scan.vitals ?? {};
  const present = PIDS.filter((spec) => vitals[spec.key] !== null && vitals[spec.key] !== undefined);

  return (
    <div className="scan-detail">
      {/* Ids carry the scan id, because several scans can be open at once and
          fixed ids would collide - every link would jump to the first one. */}
      <nav className="section-nav" aria-label="Scan sections">
        <a href={`#s${scan.id}-summary`}>Summary</a>
        <a href={`#s${scan.id}-odo`}>Odometer</a>
        {liveProps.liveScanId === scan.id && <a href={`#s${scan.id}-live`}>Live data</a>}
        {selectedCode && <a href={`#s${scan.id}-parts`}>Parts</a>}
        {scan.dtcs.length > 0 && (
          <a href={`#s${scan.id}-codes`}>
            Codes &amp; parts<span className="count">{scan.dtcs.length}</span>
          </a>
        )}
        <a href={`#s${scan.id}-vitals`}>Vitals</a>
        <a href={`#s${scan.id}-readiness`}>Emission readiness</a>
        <a href={`#s${scan.id}-diag`}>Diagnostics</a>
      </nav>

      <div id={`s${scan.id}-summary`}>
        <SummaryPanel result={toScanResult(scan)} vehicle={vehicle} scanId={scan.id} />
      </div>

      {/* A route from the verdict to what might fix it. The summary names the
          fault; this opens the parts for the code behind it without making
          someone scroll the code list and guess which one to press. */}
      {scan.dtcs.length > 0 && (
        <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
          <button
            className="primary"
            onClick={() => {
              // The most severe code, since that is the one the summary led
              // with and the one worth pricing first.
              const rank: Record<string, number> = { critical: 0, high: 1, moderate: 2, low: 3 };
              const worst = [...scan.dtcs].sort(
                (a, b) => (rank[a.severity ?? 'moderate'] ?? 2) - (rank[b.severity ?? 'moderate'] ?? 2),
              )[0];
              setSelectedCode(worst.code);
              // After the panel has rendered, or there is nothing to scroll to.
              requestAnimationFrame(() =>
                document.getElementById(`s${scan.id}-parts`)?.scrollIntoView({ block: 'start', behavior: 'smooth' }),
              );
            }}
          >
            See recommended parts
          </button>
          <span className="small dim">
            Ranked by how often each is the cause, for the most serious code with parts listed.
          </span>
        </div>
      )}

      <div id={`s${scan.id}-odo`}>
        <OdometerField scan={scan} onSaved={onOdometer} />
      </div>

      <div className="row wrap small" style={{ gap: 10, marginBottom: 10 }}>
        {scan.protocol && <span className="pill">{scan.protocol}</span>}
        {scan.transport && <span className="pill">{scan.transport}</span>}
        {scan.adapterVoltage !== null && <span className="pill">{scan.adapterVoltage.toFixed(1)} V at adapter</span>}
        {scan.supportedPids && <span className="pill">{scan.supportedPids.length} channels</span>}
        <span className="pill" title={scan.performedByName ? 'The account that ran this scan' : 'Taken before this instance had accounts'}>
          {scan.performedByName ? `run by ${scan.performedByName}` : 'no account recorded'}
        </span>
      </div>

      {/* Only the scan whose connection is open hosts the live display. Any
          other entry is a record of a past reading and cannot stream. */}
      {liveProps.liveScanId === scan.id && (
        <div id={`s${scan.id}-live`}>
          <LivePanel
            vitals={vitals}
            live={liveProps.live}
            liveKeys={liveProps.liveKeys}
            samples={liveProps.samples}
            liveHz={liveProps.liveHz}
            onToggleLiveKey={liveProps.onToggleLiveKey}
            units={units}
          />
        </div>
      )}

      {present.length > 0 && (
        <div className="vitals" id={`s${scan.id}-vitals`} style={{ marginBottom: 10 }}>
          {present.map((spec) => (
            <div className="vital" key={spec.key}>
              <span className="k">{spec.label}</span>
              <div className="v" style={{ fontSize: 15 }}>
                {formatVital(spec, vitals[spec.key] ?? null, units)}
              </div>
            </div>
          ))}
        </div>
      )}

      {scan.dtcs.length > 0 && (
        <p className="small dim" id={`s${scan.id}-codes`} style={{ margin: '0 0 6px' }}>
          Select a code to see the parts most likely to fix it, ranked by how often each one is the cause.
        </p>
      )}

      {scan.dtcs.length > 0 && (
        <div className="dtc-list" style={{ marginBottom: 12 }}>
          {/* Selecting a code shows candidate parts for it directly below,
              rather than in a panel elsewhere that has to be kept in step with
              whichever scan is open. */}
          {scan.dtcs.map((d, i) => (
            <button
              type="button"
              className={`dtc sev-${d.severity ?? 'moderate'}`}
              key={`${d.code}-${d.kind}-${i}`}
              aria-pressed={selectedCode === d.code}
              onClick={() => setSelectedCode(selectedCode === d.code ? null : d.code)}
              title={selectedCode === d.code ? 'Hide parts for this code' : 'Show candidate parts for this code'}
            >
              <span className="stripe" />
              <span className="code">{d.code}</span>
              <span className="desc">
                <span>{d.description ?? 'No description recorded'}</span>
                <span className="sys">{d.system ?? ''}</span>
              </span>
              <span className="meta">
                <span className="pill">{d.kind}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {scan.freezeFrame && (
        <section className="freeze" style={{ margin: '0 0 12px' }}>
          <header>
            <span className="eyebrow" style={{ margin: 0 }}>Freeze frame</span>
            <span className="small">
              Recorded when <span className="mono">{scan.freezeFrame.dtc}</span> set
            </span>
          </header>
          <div className="vitals">
            {PIDS.filter((spec) => scan.freezeFrame!.vitals[spec.key] != null).map((spec) => (
              <div className="vital freeze-vital" key={spec.key}>
                <span className="k">{spec.label}</span>
                <div className="v" style={{ fontSize: 15 }}>
                  {formatVital(spec, scan.freezeFrame!.vitals[spec.key] ?? null, units)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {selectedCode && (
        <div id={`s${scan.id}-parts`} style={{ marginBottom: 12 }}>
          <PartsPanel code={selectedCode} vehicle={vehicle} />
        </div>
      )}

      <div id={`s${scan.id}-readiness`}>
        <ReadinessPanel readiness={scan.readiness} />
      </div>
      {scan.monitorTests && scan.monitorTests.length > 0 && (
        <MonitorTestsPanel tests={scan.monitorTests} readiness={scan.readiness} />
      )}

      <div id={`s${scan.id}-diag`}>
        <CalibrationPanel info={scan.vehicleInfo} adapterVoltage={scan.adapterVoltage} />
      </div>

      {scan.rawLog && (
        <details>
          <summary className="small dim">Adapter traffic for this scan</summary>
          <div className="terminal" style={{ marginTop: 8, maxHeight: 260 }}>
            {scan.rawLog}
          </div>
        </details>
      )}
    </div>
  );
}

